import React from 'react';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import {
  FieldBoolean,
  FieldBooleanProps,
  FieldDate,
  FieldDateProps,
  FieldDateTime,
  FieldDateTimeProps,
  FieldTime,
  FieldTimeProps
} from './Field';
import { FieldYearSelect } from './FieldYearSelect';
import { FilterInput, FilterInputProps } from './FilterInput';

// Types
export type FilterBooleanProps = FieldBooleanProps;
export type FilterDateProps = FieldDateProps;
export type FilterDateTimeProps = FieldDateTimeProps;
export type FilterFloatProps = FilterInputProps;
export type FilterIntegerProps = FilterInputProps;
export type FilterStringProps = FilterInputProps;
export type FilterTimeProps = FieldTimeProps;
export type FilterYearProps = FilterInputProps & {
  min?: number;
  max?: number;
};

// Date
export const FilterBooleanBase = ({
  ref,
  ...props
}: FieldBooleanProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FieldBoolean ref={ref} {...props} />;
};

// Date
export const FilterDateBase = ({
  ref,
  ...props
}: FilterDateProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FieldDate ref={ref} {...props} />;
};

// DateTime
export const FilterDateTimeBase = ({
  ref,
  ...props
}: FilterDateTimeProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FieldDateTime ref={ref} {...props} />;
};

// Float
export const FilterFloatBase = ({
  ref,
  ...props
}: FilterFloatProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FilterInput ref={ref} type="number" {...props} />;
};

// Integer
export const FilterIntegerBase = ({
  ref,
  ...props
}: FilterIntegerProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FilterInput ref={ref} type="number" {...props} />;
};

// String
export const FilterStringBase = ({
  ref,
  ...props
}: FilterStringProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FilterInput ref={ref} {...props} />;
};

// Time
export const FilterTimeBase = ({
  ref,
  ...props
}: FilterTimeProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return <FieldTime ref={ref} {...props} />;
};

// Year — a year picker that shares the field chrome (trigger, rotating
// indicator, clear, keyboard nav) with the other filter selects. The year range
// and grid layout live in FieldYearSelect.
export const FilterYearBase = ({
  path,
  min,
  max,
  label,
  placeholder,
  isClearable = true,
  ...props
}: FilterYearProps) => {
  return (
    <FieldYearSelect
      path={path}
      label={label as string | undefined}
      placeholder={placeholder}
      min={min}
      max={max}
      isClearable={isClearable}
      {...props}
    />
  );
};

// Overrideable component exports
export const FilterBoolean = (props: FilterBooleanProps) =>
  useGlobalComponent('FilterBoolean', FilterBooleanBase, props);

export const FilterDate = (props: FilterDateProps) =>
  useGlobalComponent('FilterDate', FilterDateBase, props);

export const FilterDateTime = (props: FilterDateTimeProps) =>
  useGlobalComponent('FilterDateTime', FilterDateTimeBase, props);

export const FilterFloat = (props: FilterFloatProps) =>
  useGlobalComponent('FilterFloat', FilterFloatBase, props);

export const FilterInteger = (props: FilterIntegerProps) =>
  useGlobalComponent('FilterInteger', FilterIntegerBase, props);

export const FilterString = (props: FilterStringProps) =>
  useGlobalComponent('FilterString', FilterStringBase, props);

export const FilterTime = (props: FilterTimeProps) =>
  useGlobalComponent('FilterTime', FilterTimeBase, props);

export const FilterYear = (props: FilterYearProps) =>
  useGlobalComponent('FilterYear', FilterYearBase, props);
